import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Lock, Heart, Award, ArrowUpRight } from 'lucide-react';

export const Footer: React.FC = () => {
  return (
    <footer className="bg-brand-lightSec dark:bg-brand-darkBg border-t border-brand-lightBorder dark:border-brand-darkBorder text-brand-lightText dark:text-brand-darkText transition-colors duration-200">
      
      {/* Trust Badges Bar */}
      <div className="border-b border-brand-lightBorder dark:border-brand-darkBorder">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-center md:text-left">
            <div className="flex items-center gap-3 justify-center md:justify-start">
              <div className="w-10 h-10 rounded-xl bg-brand-blue/15 text-brand-blue dark:text-brand-accent flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div>
                <h4 className="font-bold text-sm">72-Hour Warranty</h4>
                <p className="text-xs text-brand-lightSecText dark:text-brand-darkSecText">Full dispute resolution & buyer protection</p>
              </div>
            </div>

            <div className="flex items-center gap-3 justify-center md:justify-start">
              <div className="w-10 h-10 rounded-xl bg-brand-blue/15 text-brand-blue dark:text-brand-accent flex items-center justify-center flex-shrink-0">
                <Lock className="w-5 h-5" />
              </div>
              <div>
                <h4 className="font-bold text-sm">Encrypted Delivery</h4>
                <p className="text-xs text-brand-lightSecText dark:text-brand-darkSecText">AES-GCM encrypted credential vault</p>
              </div>
            </div>

            <div className="flex items-center gap-3 justify-center md:justify-start">
              <div className="w-10 h-10 rounded-xl bg-brand-blue/15 text-brand-blue dark:text-brand-accent flex items-center justify-center flex-shrink-0">
                <Award className="w-5 h-5" />
              </div>
              <div>
                <h4 className="font-bold text-sm">Verified Vendors</h4>
                <p className="text-xs text-brand-lightSecText dark:text-brand-darkSecText">Vetted sellers with verified trade records</p>
              </div>
            </div>

            <div className="flex items-center gap-3 justify-center md:justify-start">
              <div className="w-10 h-10 rounded-xl bg-brand-blue/15 text-brand-blue dark:text-brand-accent flex items-center justify-center flex-shrink-0">
                <Heart className="w-5 h-5" />
              </div>
              <div>
                <h4 className="font-bold text-sm">24/7 Dispute Mediation</h4>
                <p className="text-xs text-brand-lightSecText dark:text-brand-darkSecText">Dedicated staff to mediate any issues</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer Links */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
          
          {/* Brand Info */}
          <div className="col-span-2 space-y-4">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-brand-blue flex items-center justify-center text-white">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <span className="font-extrabold text-xl tracking-tight">
                Game<span className="text-brand-blue dark:text-brand-accent">Vault</span>
              </span>
            </Link>
            <p className="text-xs text-brand-lightSecText dark:text-brand-darkSecText leading-relaxed max-w-sm">
              The premier trustworthy gaming marketplace for accounts, boosting services, in-game currencies, and instant activation keys. Powered by Cloudflare Workers and D1 SQLite.
            </p>
            <div className="text-xs text-brand-lightSecText dark:text-brand-darkSecText pt-2">
              <p className="font-medium text-brand-lightText dark:text-brand-darkText">Architecture</p>
              <p>React • Vite • TypeScript • Cloudflare Pages & Workers • D1 SQLite • R2</p>
            </div>
          </div>

          {/* Marketplace Categories */}
          <div>
            <h5 className="font-bold text-xs uppercase tracking-wider text-brand-lightSecText dark:text-brand-darkSecText mb-3">Categories</h5>
            <ul className="space-y-2 text-xs">
              <li><Link to="/browse?category=accounts" className="hover:text-brand-blue dark:hover:text-brand-accent">Game Accounts</Link></li>
              <li><Link to="/boosting" className="hover:text-brand-blue dark:hover:text-brand-accent">Boosting & Coaching</Link></li>
              <li><Link to="/browse?category=items" className="hover:text-brand-blue dark:hover:text-brand-accent">In-Game Items</Link></li>
              <li><Link to="/browse?category=currency" className="hover:text-brand-blue dark:hover:text-brand-accent">Game Gold & Currency</Link></li>
              <li><Link to="/browse?category=keys" className="hover:text-brand-blue dark:hover:text-brand-accent">Activation Keys</Link></li>
              <li><Link to="/browse?category=topup" className="hover:text-brand-blue dark:hover:text-brand-accent">Direct Top-Ups</Link></li>
            </ul>
          </div>

          {/* Popular Games */}
          <div>
            <h5 className="font-bold text-xs uppercase tracking-wider text-brand-lightSecText dark:text-brand-darkSecText mb-3">Popular Games</h5>
            <ul className="space-y-2 text-xs">
              <li><Link to="/games/valorant" className="hover:text-brand-blue dark:hover:text-brand-accent">Valorant</Link></li>
              <li><Link to="/games/league-of-legends" className="hover:text-brand-blue dark:hover:text-brand-accent">League of Legends</Link></li>
              <li><Link to="/games/counter-strike-2" className="hover:text-brand-blue dark:hover:text-brand-accent">Counter-Strike 2</Link></li>
              <li><Link to="/games/world-of-warcraft" className="hover:text-brand-blue dark:hover:text-brand-accent">World of Warcraft</Link></li>
              <li><Link to="/games/genshin-impact" className="hover:text-brand-blue dark:hover:text-brand-accent">Genshin Impact</Link></li>
              <li><Link to="/games/elden-ring" className="hover:text-brand-blue dark:hover:text-brand-accent">Elden Ring</Link></li>
            </ul>
          </div>

          {/* Trust & Legal */}
          <div>
            <h5 className="font-bold text-xs uppercase tracking-wider text-brand-lightSecText dark:text-brand-darkSecText mb-3">Trust & Legal</h5>
            <ul className="space-y-2 text-xs">
              <li><Link to="/legal?tab=terms" className="hover:text-brand-blue dark:hover:text-brand-accent">Terms of Service</Link></li>
              <li><Link to="/legal?tab=privacy" className="hover:text-brand-blue dark:hover:text-brand-accent">Privacy Policy</Link></li>
              <li><Link to="/legal?tab=refund" className="hover:text-brand-blue dark:hover:text-brand-accent">Refund & Warranty</Link></li>
              <li><Link to="/legal?tab=rules" className="hover:text-brand-blue dark:hover:text-brand-accent">Marketplace Rules</Link></li>
              <li><Link to="/legal?tab=abuse" className="hover:text-brand-blue dark:hover:text-brand-accent">Abuse Reporting</Link></li>
              <li><Link to="/become-seller" className="hover:text-brand-blue dark:hover:text-brand-accent flex items-center gap-1 font-semibold text-brand-blue dark:text-brand-accent">Seller Application <ArrowUpRight className="w-3 h-3" /></Link></li>
            </ul>
          </div>
        </div>

        {/* Bottom Legal / Demo Disclaimer */}
        <div className="border-t border-brand-lightBorder dark:border-brand-darkBorder mt-10 pt-6 flex flex-col md:flex-row items-center justify-between gap-4 text-[11px] text-brand-lightSecText dark:text-brand-darkSecText">
          <p>© 2026 GameVault. All rights reserved. Demo marketplace with simulated transactions.</p>
          <p className="text-center md:text-right">
            All game titles, artwork, and trademarks are the property of their respective publishers (Riot Games, Valve, Blizzard, HoYoverse, Epic Games, FromSoftware).
          </p>
        </div>
      </div>
    </footer>
  );
};
